import React, { useEffect, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { errMsg } from '@/services/staffAttendanceApi';
import { tallyApi } from '@/services/ewayBillApi';
import { fmtDT } from '@/components/ewayBill/shared';

const DAYS = [7, 30, 90, 180, 365];
const SHOW = 200;
const STEPS = [
  'Download the Excel below and copy it to the Tally PC.',
  'In TallyPrime: Alt+G → Create Party Using GSTIN/UIN → Alt+L (Import GSTIN/UIN from Excel).',
  'Pick the file, worksheet “GSTIN”, column A → Ctrl+A. Tally fetches the details from the GST portal (log in to e-Way Bill / e-Invoice if asked). Check them and press Ctrl+A to save.',
  'Back in WMS press “Re-read Tally masters” — WMS matches these ledgers by GSTIN, so names don’t have to match.',
];

const Stat = ({ l, n, id, cls = '' }) => (
  <div className="rounded-lg border border-gray-200 bg-white px-3 py-2"><div className="text-[11px] uppercase text-gray-500">{l}</div>
    <div className={`text-xl font-bold tabular-nums ${cls}`} data-testid={`gstin-stat-${id}`}>{n}</div></div>
);

const Rows = ({ rows }) => (
  <div className="max-h-72 overflow-y-auto rounded-lg border border-gray-200 bg-white" data-testid="gstin-list">
    <table className="w-full text-sm">
      <thead className="sticky top-0 bg-gray-50 text-left text-[11px] uppercase text-gray-500">
        <tr><th className="px-3 py-1.5">GSTIN</th><th className="px-3 py-1.5">Customer</th><th className="px-3 py-1.5">Place · State</th><th className="px-3 py-1.5 text-right">DCs</th></tr>
      </thead>
      <tbody className="divide-y">
        {rows.slice(0, SHOW).map((r) => (
          <tr key={r.gstin} data-testid={`gstin-row-${r.gstin}`}>
            <td className="px-3 py-1.5 font-mono text-xs">{r.gstin}</td>
            <td className="px-3 py-1.5">{r.name}{r.legal_name && r.legal_name !== r.name && <span className="block text-xs text-gray-500">{r.legal_name}</span>}</td>
            <td className="px-3 py-1.5 text-gray-600">{[r.place, r.state].filter(Boolean).join(' · ')}</td>
            <td className="px-3 py-1.5 text-right tabular-nums">{r.dcs}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/** Customers on recent DCs whose GSTIN isn't in Tally → Excel for Tally's own "Create Party Using GSTIN/UIN". */
export const GstinPartiesDialog = ({ open, onClose }) => {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setD(null);
    tallyApi.gstinParties(days).then(setD).catch((e) => toast.error(errMsg(e, 'Failed to load')));
  }, [open, days]);
  const download = () => {
    setBusy(true);
    tallyApi.gstinXlsx(days).then(() => toast.success('Excel downloaded')).catch((e) => toast.error(errMsg(e, 'Download failed'))).finally(() => setBusy(false));
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="gstin-dialog">
        <DialogHeader><DialogTitle>Create customers in Tally by GSTIN</DialogTitle></DialogHeader>
        <ol className="list-decimal pl-5 space-y-1 text-sm text-gray-700" data-testid="gstin-steps">{STEPS.map((s) => <li key={s}>{s}</li>)}</ol>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-gray-600">Customers on DCs of the last</span>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="h-9 rounded-md border border-gray-300 bg-white px-2" data-testid="gstin-days">
            {DAYS.map((n) => <option key={n} value={n}>{n} days</option>)}
          </select>
          <Button onClick={download} disabled={busy || !d?.rows.length} className="ml-auto bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="gstin-download-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}Download Excel ({d?.rows.length ?? 0})
          </Button>
        </div>
        {!d ? <Loader2 className="w-6 h-6 mx-auto my-8 animate-spin text-amber-600" /> : (
          <div className="space-y-3">
            {!d.masters_synced_at ? <p className="text-xs text-amber-700" data-testid="gstin-no-masters">Tally masters not read yet — every GSTIN is listed. Run the Tally link and press “Re-read Tally masters” first.</p>
              : <p className="text-xs text-gray-500">Checked against Tally masters read {fmtDT(d.masters_synced_at)}.</p>}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Stat l="Customers on DCs" n={d.customers} id="customers" />
              <Stat l="GSTIN not in Tally" n={d.rows.length} id="missing" cls="text-sky-700" />
              <Stat l="Already in Tally" n={d.in_tally} id="in-tally" cls="text-emerald-700" />
              <Stat l="No GSTIN (unregistered)" n={d.unregistered} id="urp" cls="text-gray-600" />
            </div>
            {d.unregistered > 0 && <p className="text-xs text-gray-500">Unregistered customers can't be fetched by GSTIN — WMS creates them in Tally from Customer Master when their e-way bill is sent.</p>}
            {d.unclear.length > 0 && (
              <p className="text-xs text-amber-700" data-testid="gstin-unclear">{d.unclear.length} customer name(s) match several Customer Master entries and are left out: {d.unclear.slice(0, 8).join(', ')}{d.unclear.length > 8 ? ' …' : ''}</p>
            )}
            {d.rows.length === 0 ? <p className="py-6 text-center text-sm text-gray-500" data-testid="gstin-empty">Every customer GSTIN on these DCs is already in Tally</p> : <Rows rows={d.rows} />}
            {d.rows.length > SHOW && <p className="text-xs text-gray-500">Showing {SHOW} of {d.rows.length} — the Excel has all of them.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GstinPartiesDialog;
